/**
 * Human-readable names for where a popup was shown, for the popup report's
 * Screen column.
 *
 * The app reports the screen under the name it was configured with in the
 * popup editor (`knowledge-article`, `baby-mum`, ...). The underscore names
 * are kept for entries recorded before that.
 */

const SCREEN_NAMES = {
  home: 'Home főoldal',
  'baby-mum': 'Hétről hétre',
  knowledge: 'Tudástár főoldal',
  'knowledge-category': 'Kategória',
  'knowledge-article': 'Cikk',
  'knowledge-faq': 'Tudástár GYIK',
  tools: 'Eszközök főoldal',
  todo: 'Teendők főoldal',
  teladoc: 'Teladoc',
  photographer: 'Fotósok',
  timer: 'Időzítő',
  diary: 'Napló',
  'diary-details': 'Napló bejegyzés',
  'food-safety': 'Élelmiszer-biztonság',
  'recipe-detail': 'Recept kategória',
  'recipe-article': 'Recept',
  'weight-curve': 'Súlygörbe',
  deals: 'Kuponok',
  // legacy names
  knowledge_category: 'Kategória',
  knowledge_article: 'Cikk',
};

const findTitle = (items, id) => items.find((item) => String(item?.id) === String(id))?.title;

const placementName = (screen, screenId, knowledge = []) => {
  const name = SCREEN_NAMES[screen];
  const hasId = screenId !== undefined && screenId !== null && screenId !== '';

  switch (screen) {
    case 'home_baby':
      return `Home baba - ${screenId}. hét`;
    case 'home_mum':
      return `Home mama - ${screenId}. hét`;
    case 'tools_category':
      return `Eszköz: ${screenId}`;
    case 'baby-mum':
      return hasId ? `${name} - ${Number(screenId) + 1}. fül` : name;
    case 'knowledge-category':
    case 'knowledge_category': {
      const title = hasId && findTitle(knowledge, screenId);
      return title ? `${name}: ${title}` : name;
    }
    case 'knowledge-article':
    case 'knowledge_article': {
      const articles = knowledge.flatMap((category) => category?.articles ?? []);
      const title = hasId && findTitle(articles, screenId);
      return title ? `${name}: ${title}` : name;
    }
    default:
      return name ?? screen ?? '';
  }
};

/**
 * Names every distinct place a popup was shown. Entries recorded before the
 * app sent the article/category id have an empty id; they are dropped when the
 * same screen also has entries with an id, so a popup pinned to one article
 * reads "Cikk: <title>" instead of "Cikk, Cikk: <title>".
 *
 * `placements` (screen + id pairs) comes from newer backends; older ones only
 * return the separate `screen` list, whose positions don't match `screenId`.
 */
export const popupPlacementNames = (row, knowledge = []) => {
  const placements = row?.placements ?? (row?.screen ?? []).map((screen) => ({ screen }));
  const withId = new Set(placements.filter((p) => p.screenId).map((p) => p.screen));
  const names = placements
    .filter((p) => p.screenId || !withId.has(p.screen))
    .map((p) => placementName(p.screen, p.screenId, knowledge))
    .filter(Boolean);
  return [...new Set(names)].join(', ');
};
